import axios from 'axios'
import type {
  IClaimDashboard,
  IClaimDetails,
  IClaimFilters,
  IClaimStatusUpdate,
  IClaimStatusesResponse,
  ClaimFormData,
  UpdateClaimFormData,
  IClaimCostsForm,
  CreateClaimInput,
  UpdateClaimInput,
  IClaimHistoryFilters,
} from '@/types/asicw/claims'
import type { IPage } from '@/types/shared/pagination'

const ASICW_API = import.meta.env.VITE_APP_ASICW_API
const CLAIMS_ENDPOINT = import.meta.env.VITE_APP_ASICW_CLAIMS_ENDPOINT

function transformToCreateFormData(claims: CreateClaimInput[]): ClaimFormData {
  const formData = new FormData() as ClaimFormData

  claims.forEach((claim, claimIndex) => {
    formData.append(`policy_claims[${claimIndex}]policy_id`, claim.policy_id.toString())
    formData.append(`policy_claims[${claimIndex}]date_of_loss`, claim.date_of_loss)

    claim.failures.forEach((failure, failureIndex) => {
      formData.append(`policy_claims[${claimIndex}]failures[${failureIndex}]part`, failure.part)
      formData.append(`policy_claims[${claimIndex}]failures[${failureIndex}]reason`, failure.reason)
      if (failure.part_sn)
        formData.append(`policy_claims[${claimIndex}]failures[${failureIndex}]part_sn`, failure.part_sn)
      if (failure.part_model)
        formData.append(`policy_claims[${claimIndex}]failures[${failureIndex}]part_model`, failure.part_model)
      if (failure.part_number)
        formData.append(
          `policy_claims[${claimIndex}]failures[${failureIndex}]part_number`,
          failure.part_number.toString()
        )
    })

    if (claim.notes) {
      formData.append(`policy_claims[${claimIndex}]notes`, claim.notes)
    }

    claim.pictures?.forEach((picture, pictureIndex) => {
      formData.append(`policy_claims[${claimIndex}]pictures[${pictureIndex}]`, picture)
    })

    claim.log_files?.forEach((logFile, logFileIndex) => {
      formData.append(`policy_claims[${claimIndex}]log_files[${logFileIndex}]`, logFile)
    })

    if (claim.container) {
      formData.append(`policy_claims[${claimIndex}]container.type`, claim.container.type)
      formData.append(`policy_claims[${claimIndex}]container.length`, claim.container.length.toString())
      formData.append(`policy_claims[${claimIndex}]container.width`, claim.container.width.toString())
      formData.append(`policy_claims[${claimIndex}]container.height`, claim.container.height.toString())
      formData.append(`policy_claims[${claimIndex}]container.weight`, claim.container.weight.toString())
    }
  })

  return formData
}

function transformToUpdateFormData(data: UpdateClaimInput): UpdateClaimFormData {
  const formData = new FormData() as UpdateClaimFormData

  data.failures.forEach((failure, index) => {
    formData.append(`failures[${index}]part`, failure.part)
    formData.append(`failures[${index}]reason`, failure.reason)
    if (failure.part_sn) formData.append(`failures[${index}]part_sn`, failure.part_sn)
    if (failure.part_model) formData.append(`failures[${index}]part_model`, failure.part_model)
    if (failure.part_number) formData.append(`failures[${index}]part_number`, failure.part_number.toString())
  })

  if (data.notes) {
    formData.append('notes', data.notes)
  }

  if (data.created_at) {
    formData.append('created_at', data.created_at)
  }

  if (data.date_of_loss) {
    formData.append('date_of_loss', data.date_of_loss)
  }

  data.deletedPictureIds?.forEach((id, index) => {
    formData.append(`deleted_pictures[${index}]`, id.toString())
  })

  data.newPictures?.forEach((picture, index) => {
    formData.append(`new_pictures[${index}]`, picture)
  })

  data.deletedLogFileIds?.forEach((id, index) => {
    formData.append(`deleted_log_files[${index}]`, id.toString())
  })

  data.newLogFiles?.forEach((logFile, index) => {
    formData.append(`new_log_files[${index}]`, logFile)
  })

  if (data.container) {
    formData.append('container.type', data.container.type)
    formData.append('container.length', data.container.length.toString())
    formData.append('container.width', data.container.width.toString())
    formData.append('container.height', data.container.height.toString())
    formData.append('container.weight', data.container.weight.toString())
  }

  return formData
}

/**
 * Creates a new claim
 * @param claims Array of claim data to create
 */
export async function createClaim(claims: CreateClaimInput[]) {
  const formData = transformToCreateFormData(claims)
  const res = await axios.post(CLAIMS_ENDPOINT, formData)
  return res.data
}

/**
 * Gets claims with pagination and filters
 * @param filters The filters to apply to the query
 */
export async function getClaims(filters: IClaimFilters): Promise<IPage<IClaimDashboard>> {
  const res = await axios.get(CLAIMS_ENDPOINT, { params: filters })
  return res.data
}

/**
 * Gets claim details by ID
 * @param claimNumber The claim Number
 */
export async function getClaimDetails(claimNumber: string): Promise<IClaimDetails> {
  const res = await axios.get(`${CLAIMS_ENDPOINT}/${claimNumber}`)
  return res.data
}

/**
 * Updates a claim
 * @param claimNumber The claim Number
 * @param data The data containing the updated claim information
 */
export async function updateClaim(claimNumber: string, data: UpdateClaimInput) {
  const formData = transformToUpdateFormData(data)
  const res = await axios.patch(`${CLAIMS_ENDPOINT}/${claimNumber}`, formData)
  return res.data
}

/**
 * Deletes a claim
 * @param claimNumber The claim Number to delete
 */
export async function deleteClaim(claimNumber: string) {
  const res = await axios.delete(`${CLAIMS_ENDPOINT}/${claimNumber}`)
  return res.data
}

/**
 * Deletes multiple claims
 * @param claimIds The claim IDs to delete
 */
export async function deleteBulkClaims(claimIds: string[]) {
  const res = await axios.delete(`${CLAIMS_ENDPOINT}`, { data: { claim_ids: claimIds } })
  return res.data
}

/**
 * Gets available claim statuses and reject reasons
 */
export async function getClaimStatuses(): Promise<IClaimStatusesResponse> {
  const res = await axios.get(`${ASICW_API}/claim-statuses`)
  return res.data
}

/**
 * Updates a claim's status
 * @param claimNumber The claim Number
 * @param statusUpdate The status update data
 */
export async function updateClaimStatus(claimNumber: string, statusUpdate: IClaimStatusUpdate) {
  const res = await axios.patch(`${CLAIMS_ENDPOINT}/${claimNumber}/status`, statusUpdate)
  return res.data
}

/**
 * Updates a claim's costs
 * @param claimNumber The claim Number
 * @param form The costs data
 */
export async function updateClaimCosts(claimNumber: string, form: IClaimCostsForm) {
  const res = await axios.put(`${CLAIMS_ENDPOINT}/${claimNumber}/costs`, form)
  return res.data
}

export async function getClaimHistory(claimNumber: string, filters: IClaimHistoryFilters) {
  const res = await axios.get(`${CLAIMS_ENDPOINT}/${claimNumber}/history`, { params: filters })
  return res.data
}
